import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import z from 'zod';
import { updatePlanSchema } from './schema';
import { InlineEditInput } from '@/components/shared/inline-edit-input';
import { MarkdownRendererInlineEdit } from '@/components/shared/markdown-renderer-inline-edit';
import { useNotification } from '@/context/notification';
import { useUpdatePlan } from '@/data/idp/plans';
import { PlanStatus, type Plan } from '@/data/idp/plans/interfaces';
import { formatDate } from '@/utils/format-date';
import { getMonthLabel } from '@/utils/masks/time';
import { IdpEvent } from '@/constants/analytics';
import { buildIdpPlanProperties } from '@/packages/idp/utils/idp-analytics';
import { track } from '@/utils/analytics';

const defaultValues = {
  title: '',
  description: '',
  duration_in_months: null,
};

interface PlanSummaryValues {
  title: string | null;
  description?: string;
  duration_in_months: number | null;
}

const getChangedFields = (values: PlanSummaryValues, plan: Plan) => {
  const changes = {
    title: (values.title || '') !== (plan.title || ''),
    description: (values.description || '') !== (plan.description || ''),
    duration: values.duration_in_months !== plan.duration_in_months,
  };

  return (Object.keys(changes) as (keyof typeof changes)[]).filter(
    (field) => changes[field]
  );
};

interface DateDisplayProps {
  title: string;
  date?: string | null;
}

const DateDisplay = ({ title, date }: DateDisplayProps) => (
  <FormControl fullWidth>
    <FormLabel sx={{ marginBottom: '0' }}>
      <Typography>
        <b>{title}:</b> {formatDate(date || '')}
      </Typography>
    </FormLabel>
  </FormControl>
);

export const StartedAt = ({ plan }: { plan: Plan }) =>
  plan.status === PlanStatus.Draft ? null : (
    <DateDisplay title="Início" date={plan.started_at} />
  );

export const EstimatedEndsAt = ({ plan }: { plan: Plan }) =>
  plan.status !== PlanStatus.InProgress ? null : (
    <DateDisplay title="Fim estimado" date={plan.estimated_ends_at} />
  );

export const EndedAt = ({ plan }: { plan: Plan }) =>
  plan.status !== PlanStatus.Closed ? null : (
    <DateDisplay title="Fim (data da conclusão)" date={plan.ended_at} />
  );

export const PlanSummary = ({ plan }: { plan: Plan }) => {
  const { mutate: updatePlan } = useUpdatePlan();
  const { toast } = useNotification();
  const { control, getValues, reset } = useForm({
    defaultValues,
    resolver: zodResolver(updatePlanSchema),
  });

  const handleChange = () => {
    const values = { ...getValues() };
    updatePlan(
      {
        id: plan.id,
        title: values.title,
        description: values.description,
        duration_in_months: values.duration_in_months,
      },
      {
        onSuccess: () => {
          track(IdpEvent.PlanEdited, {
            ...buildIdpPlanProperties(plan),
            fields_changed: getChangedFields(values, plan),
          });
          toast({
            content: 'Plano atualizado.',
            type: 'success',
          });
        },
        onError: () => {
          toast({
            content: 'Erro ao atualizar o plano. Tente novamente.',
            type: 'error',
          });
        },
      }
    );
  };

  const mountDurationOptions = () => {
    const options = [];
    for (let i = 1; i <= 18; i += 1) {
      options.push(
        <MenuItem key={i} value={i}>
          {getMonthLabel(i)}
        </MenuItem>
      );
    }
    return options;
  };

  const handleInlineEditFieldsChange = async (
    fieldOnChange: (value: string) => void,
    value: string
  ) => {
    try {
      fieldOnChange(value);
      await handleChange();

      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    const values = {
      title: plan?.title || defaultValues.title,
      description: plan?.description || defaultValues.description,
      duration_in_months: plan?.duration_in_months
        ? plan?.duration_in_months
        : defaultValues.duration_in_months,
    };

    reset(values);
  }, [plan, reset]);

  return (
    <Stack gap={4} flex={1}>
      <Controller
        control={control}
        name="title"
        render={({ field, fieldState: { error } }) => (
          <FormControl error={!!error} fullWidth>
            <FormLabel htmlFor="description">
              <Typography fontWeight="semibold">Título</Typography>
            </FormLabel>

            <Typography
              component={InlineEditInput}
              disabled={plan.status === PlanStatus.Closed}
              error={!!error}
              fullWidth
              onAccept={(value) =>
                handleInlineEditFieldsChange(field.onChange, value)
              }
              onClickOutside={(value) =>
                handleInlineEditFieldsChange(field.onChange, value)
              }
              placeholder="Dê um título para o seu plano"
              value={field.value || ''}
              variant="h1"
            />
          </FormControl>
        )}
      />
      <Controller
        control={control}
        name="description"
        render={({ field, fieldState: { error } }) => (
          <FormControl error={!!error} fullWidth>
            <FormLabel htmlFor="description">
              <Typography fontWeight="semibold">Descrição</Typography>
            </FormLabel>
            <MarkdownRendererInlineEdit
              editable={plan.status !== PlanStatus.Closed}
              markdown={field.value || ''}
              placeholder="Adicionar descrição"
              onSave={(value) =>
                handleInlineEditFieldsChange(field.onChange, value)
              }
              validationSchema={z.string().optional()}
            />
          </FormControl>
        )}
      />
      <Stack
        direction={{ md: 'row' }}
        alignItems={{ md: 'center' }}
        gap={{ xs: 2, md: 4 }}
      >
        <Stack direction="row" alignItems="center">
          <Controller
            name="duration_in_months"
            control={control}
            render={({ field }) => {
              return (
                <FormControl
                  sx={{
                    flexDirection: 'row',
                    alignItems: 'baseline',
                  }}
                >
                  <FormLabel htmlFor="duration_in_months">
                    <Typography fontWeight="bold" mr={1}>
                      Duração:
                    </Typography>
                  </FormLabel>
                  <Select
                    {...field}
                    variant="outlined"
                    displayEmpty
                    id="duration_in_months"
                    disabled={plan.status === PlanStatus.Closed}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      handleChange();
                    }}
                    renderValue={(value) =>
                      value ? getMonthLabel(value) : 'Adicionar duração'
                    }
                    size="small"
                    value={field.value || ''}
                  >
                    {mountDurationOptions()}
                  </Select>
                </FormControl>
              );
            }}
          />
        </Stack>

        {plan.status !== PlanStatus.Draft && (
          <Stack direction="row" alignItems="center">
            <StartedAt plan={plan} />
          </Stack>
        )}
        {plan.status === PlanStatus.InProgress && (
          <Stack direction="row" alignItems="center">
            <EstimatedEndsAt plan={plan} />
          </Stack>
        )}
        {plan.status === PlanStatus.Closed && (
          <Stack direction="row" alignItems="center">
            <EndedAt plan={plan} />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
